/**
 * Formatters for lab data. D1 stores timestamps as strings like
 * "2026-04-10 19:26:39" — we render them as relative time or short dates.
 */

/** Safely parse a JSON-stringified field. Returns fallback on error. */
export function parseJson<T>(raw: string | null | undefined, fallback: T): T {
  if (raw === null || raw === undefined || raw === "") return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/** Short form of a ULID-ish id: "exp_bade0579-9e30-..." → "bade0579". */
export function shortId(id: string | null | undefined): string {
  if (!id) return "—";
  const parts = id.split("_");
  const tail = parts.length > 1 ? parts[1] : parts[0];
  return tail.slice(0, 8);
}

/** Short git hash — first 7 chars. */
export function shortHash(hash: string | null | undefined): string {
  if (!hash) return "—";
  return hash.slice(0, 7);
}

/** Format fitness to 4 decimal places, with a dash for null. */
export function formatFitness(f: number | null | undefined): string {
  if (f === null || f === undefined) return "—";
  return f.toFixed(4);
}

/** Absolute short date: "Apr 10, 19:26" */
export function formatDate(ts: string | null | undefined): string {
  if (!ts) return "—";
  const d = parseTimestamp(ts);
  if (!d) return ts;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Relative time: "3m ago", "2h ago", "yesterday", "3d ago", "Apr 5". */
export function formatRelative(ts: string | null | undefined): string {
  if (!ts) return "—";
  const d = parseTimestamp(ts);
  if (!d) return ts;
  const now = Date.now();
  const diff = now - d.getTime();
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * D1 `datetime('now')` returns "YYYY-MM-DD HH:MM:SS" UTC. The Date
 * constructor misreads that as local on some engines, so we explicitly
 * convert to ISO with a Z suffix first.
 */
function parseTimestamp(raw: string): Date | null {
  if (!raw) return null;
  if (raw.includes("T")) {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }
  const iso = raw.replace(" ", "T") + "Z";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}
